import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ClipboardCopy } from '@/components/content/previews/ClipboardCopy';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  Image, 
  Video, 
  Headphones,
  Cpu,
  Zap,
  Star,
  TrendingUp,
  Users,
  Settings,
  AlertCircle,
  Sparkles,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

interface CreditCostStructure {
  content: {
    base_generation: number;
    manual_generation: number;
    improvement: number;
    content_types: {
      [key: string]: number;
    };
  };
  media: {
    image: {
      [key: string]: number;
    };
    video: {
      [key: string]: number;
    };
    audio: {
      [key: string]: number;
    };
  };
  processing: {
    [key: string]: number;
  };
  api: {
    standard_call: number;
    bulk_operation: number;
    webhook_call: number;
    llm_call: {
      [key: string]: number;
    };
  };
  features: {
    [key: string]: number;
  };
}

interface CreditPackage {
  credits: number;
  price: number;
  label: string;
  popular: boolean;
}

interface CostsResponse {
  costs: CreditCostStructure;
  packages: CreditPackage[];
}

// Utility function to generate formatted markdown from credit costs data
function generateMarkdown(costs: CreditCostStructure, packages: CreditPackage[]): string {
  const formatOperationName = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatCredits = (value: number) => {
    return value === 0 ? 'Free' : `${value} credits`;
  };

  let markdown = '# Credit Cost Structure\n\n';
  markdown += 'Complete breakdown of credit costs for all platform operations and features.\n\n';

  // Content Generation Section
  if (costs.content) {
    markdown += '## Content Generation\n\n';
    
    // Base operations
    markdown += '### Base Operations\n\n';
    if (costs.content.base_generation !== undefined) {
      markdown += `- **Base Generation**: ${formatCredits(costs.content.base_generation)}\n`;
    }
    if (costs.content.manual_generation !== undefined) {
      markdown += `- **Manual Generation**: ${formatCredits(costs.content.manual_generation)}\n`;
    }
    if (costs.content.improvement !== undefined) {
      markdown += `- **Content Improvement**: ${formatCredits(costs.content.improvement)}\n`;
    }

    // Content Types
    if (costs.content.content_types && Object.keys(costs.content.content_types).length > 0) {
      markdown += '\n### Content Types\n\n';
      markdown += '| Content Type | Credits |\n';
      markdown += '|--------------|----------|\n';
      Object.entries(costs.content.content_types).forEach(([key, value]) => {
        markdown += `| ${formatOperationName(key)} | ${formatCredits(value)} |\n`;
      });
    }
    markdown += '\n';
  }

  // Media Costs Section
  if (costs.media) {
    markdown += '## Media Operations\n\n';
    
    if (costs.media.image && Object.keys(costs.media.image).length > 0) {
      markdown += '### Image Operations\n\n';
      markdown += '| Operation | Credits |\n';
      markdown += '|-----------|----------|\n';
      Object.entries(costs.media.image).forEach(([key, value]) => {
        markdown += `| ${formatOperationName(key)} | ${formatCredits(value)} |\n`;
      });
      markdown += '\n';
    }

    if (costs.media.video && Object.keys(costs.media.video).length > 0) {
      markdown += '### Video Operations\n\n';
      markdown += '| Operation | Credits |\n';
      markdown += '|-----------|----------|\n';
      Object.entries(costs.media.video).forEach(([key, value]) => {
        markdown += `| ${formatOperationName(key)} | ${formatCredits(value)} |\n`;
      });
      markdown += '\n';
    }

    if (costs.media.audio && Object.keys(costs.media.audio).length > 0) {
      markdown += '### Audio Operations\n\n';
      markdown += '| Operation | Credits |\n';
      markdown += '|-----------|----------|\n';
      Object.entries(costs.media.audio).forEach(([key, value]) => {
        markdown += `| ${formatOperationName(key)} | ${formatCredits(value)} |\n`;
      });
      markdown += '\n';
    }
  }

  // Processing Section
  if (costs.processing && Object.keys(costs.processing).length > 0) {
    markdown += '## Data Processing\n\n';
    markdown += 'Costs for data processing and analysis operations.\n\n';
    markdown += '| Operation | Credits |\n';
    markdown += '|-----------|----------|\n';
    Object.entries(costs.processing).forEach(([key, value]) => {
      markdown += `| ${formatOperationName(key)} | ${formatCredits(value)} |\n`;
    });
    markdown += '\n';
  }

  // API Section
  if (costs.api) {
    markdown += '## API Operations\n\n';
    markdown += 'Costs for API calls and external service integrations.\n\n';
    
    if (costs.api.standard_call !== undefined) {
      markdown += `- **Standard Call**: ${formatCredits(costs.api.standard_call)}\n`;
    }
    if (costs.api.bulk_operation !== undefined) {
      markdown += `- **Bulk Operation**: ${formatCredits(costs.api.bulk_operation)}\n`;
    }
    if (costs.api.webhook_call !== undefined) {
      markdown += `- **Webhook Call**: ${formatCredits(costs.api.webhook_call)}\n`;
    }

    if (costs.api.llm_call && Object.keys(costs.api.llm_call).length > 0) {
      markdown += '\n### LLM API Calls\n\n';
      markdown += '| Service | Credits |\n';
      markdown += '|---------|----------|\n';
      Object.entries(costs.api.llm_call).forEach(([key, value]) => {
        markdown += `| ${formatOperationName(key)} | ${formatCredits(value)} |\n`;
      });
    }
    markdown += '\n';
  }

  // Features Section
  if (costs.features && Object.keys(costs.features).length > 0) {
    markdown += '## Advanced Features\n\n';
    markdown += 'Costs for advanced platform features and automation.\n\n';
    markdown += '| Feature | Credits |\n';
    markdown += '|---------|----------|\n';
    Object.entries(costs.features).forEach(([key, value]) => {
      markdown += `| ${formatOperationName(key)} | ${formatCredits(value)} |\n`;
    });
    markdown += '\n';
  }

  // Credit Packages Section
  if (packages && packages.length > 0) {
    markdown += '## Credit Packages\n\n';
    markdown += 'Available credit packages for purchase.\n\n';
    markdown += '| Package | Credits | Price | Rate per 1k Credits |\n';
    markdown += '|---------|---------|-------|-----------------------|\n';
    packages.forEach(pkg => {
      const ratePerK = (pkg.price / pkg.credits * 1000).toFixed(1);
      const popular = pkg.popular ? ' ⭐' : '';
      markdown += `| ${pkg.label}${popular} | ${pkg.credits.toLocaleString()} | $${pkg.price} | $${ratePerK} |\n`;
    });
    markdown += '\n';
    
    if (packages.some(pkg => pkg.popular)) {
      markdown += '*⭐ = Popular choice*\n\n';
    }
  }

  markdown += '---\n\n';
  markdown += `*Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}*\n`;

  return markdown;
}

export function CreditStructure() {
  const [costs, setCosts] = useState<CreditCostStructure | null>(null);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['content']));

  // Generate markdown when costs or packages change
  const markdownContent = useMemo(() => {
    if (!costs) return '';
    return generateMarkdown(costs, packages);
  }, [costs, packages]);

  useEffect(() => {
    async function fetchCosts() {
      try {
        // Get session directly to ensure we have the latest, following the same pattern as useCredits
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) {
          throw new Error('No authentication session found');
        }

        const workerUrl = import.meta.env.VITE_WORKERS_URL;
        const response = await fetch(`${workerUrl}/api/credits/costs`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${currentSession.access_token}`
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch credit costs');
        }

        const data: CostsResponse = await response.json();
        setCosts(data.costs);
        setPackages(data.packages);
      } catch (err) {
        console.error('Error fetching credit costs:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchCosts();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-96" />
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !costs) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load credit structure: {error || 'Unknown error'}
        </AlertDescription>
      </Alert>
    );
  }

  const formatOperationName = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'content': return <FileText className="w-5 h-5" />;
      case 'media': return <Image className="w-5 h-5" />;
      case 'processing': return <Cpu className="w-5 h-5" />;
      case 'api': return <Zap className="w-5 h-5" />;
      case 'features': return <Star className="w-5 h-5" />;
      default: return <Settings className="w-5 h-5" />;
    }
  };

  const getSubcategoryIcon = (subcategory: string) => {
    switch (subcategory) {
      case 'image': return <Image className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'audio': return <Headphones className="w-4 h-4" />;
      case 'content_types': return <FileText className="w-4 h-4" />;
      case 'llm_call': return <Sparkles className="w-4 h-4" />;
      default: return null;
    }
  };

  const renderCostItem = (key: string, value: number, level: number = 0) => {
    const indentClass = level === 0 ? '' : level === 1 ? 'ml-4' : 'ml-8';
    
    return (
      <div key={key} className={`flex justify-between items-center py-2 ${indentClass}`}>
        <div className="flex items-center gap-2">
          {level === 1 && getSubcategoryIcon(key)}
          <span className={`${level === 0 ? 'font-medium' : level === 1 ? 'font-normal' : 'text-muted-foreground'}`}>
            {formatOperationName(key)}
          </span>
        </div>
        <Badge variant={value === 0 ? 'secondary' : 'default'} className="font-mono">
          {value === 0 ? 'Free' : `${value} credits`}
        </Badge>
      </div>
    );
  };

  const renderNestedCosts = (obj: any, level: number = 0): React.ReactNode[] => {
    return Object.entries(obj).map(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        return (
          <div key={key}>
            <div className={`flex items-center gap-2 py-2 font-medium ${level === 0 ? 'text-base' : 'text-sm'} ${level > 0 ? 'ml-4' : ''}`}>
              {level === 1 && getSubcategoryIcon(key)}
              {formatOperationName(key)}
            </div>
            <div className={level === 0 ? 'ml-4' : 'ml-8'}>
              {renderNestedCosts(value, level + 1)}
            </div>
            {level === 0 && <Separator className="my-3" />}
          </div>
        );
      } else if (typeof value === 'number') {
        return renderCostItem(key, value, level);
      }
      return null;
    }).filter(Boolean);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Overview */}
      <Card className="bg-white rounded-xl border border-gray-200/50 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center shadow-lg">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-1">
                  Credit Cost Structure
                </h3>
                <p className="text-sm text-purple-700 dark:text-purple-200">
                  Complete breakdown of credit costs for all platform operations and features
                </p>
              </div>
            </div>
            {costs && (
              <div className="flex gap-2">
                <ClipboardCopy
                  text={markdownContent}
                  variant="button"
                  labelText="Export as Markdown"
                  className="bg-white/80 backdrop-blur-sm border-purple-200 hover:bg-purple-50 hover:border-purple-300 transition-all duration-200"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white/80 backdrop-blur-sm border-purple-200 hover:bg-purple-50 hover:border-purple-300 transition-all duration-200"
                  onClick={() => {
                    const allSections = Object.keys(costs);
                    if (expandedSections.size === allSections.length) {
                      setExpandedSections(new Set());
                    } else {
                      setExpandedSections(new Set(allSections));
                    }
                  }}
                >
                  {expandedSections.size === Object.keys(costs).length ? 'Collapse All' : 'Expand All'}
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="h-2 bg-gradient-to-r from-purple-200 via-violet-200 to-indigo-200 dark:from-purple-800 dark:via-violet-800 dark:to-indigo-800" />
      </Card>

      {/* Enhanced Credit Categories */}
      <div className="grid gap-6">
        {Object.entries(costs).map(([category, categoryData]) => {
          const isExpanded = expandedSections.has(category);
          const categoryColors = {
            content: { gradient: 'from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20', iconGradient: 'from-blue-500 to-indigo-600', textColor: 'text-blue-900 dark:text-blue-100', subtextColor: 'text-blue-700 dark:text-blue-200', bottomGradient: 'from-blue-200 via-indigo-200 to-purple-200 dark:from-blue-800 dark:via-indigo-800 dark:to-purple-800' },
            media: { gradient: 'from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20', iconGradient: 'from-green-500 to-emerald-600', textColor: 'text-green-900 dark:text-green-100', subtextColor: 'text-green-700 dark:text-green-200', bottomGradient: 'from-green-200 via-emerald-200 to-teal-200 dark:from-green-800 dark:via-emerald-800 dark:to-teal-800' },
            processing: { gradient: 'from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20', iconGradient: 'from-orange-500 to-red-600', textColor: 'text-orange-900 dark:text-orange-100', subtextColor: 'text-orange-700 dark:text-orange-200', bottomGradient: 'from-orange-200 via-red-200 to-pink-200 dark:from-orange-800 dark:via-red-800 dark:to-pink-800' },
            api: { gradient: 'from-cyan-50 to-teal-50 dark:from-cyan-950/20 dark:to-teal-950/20', iconGradient: 'from-cyan-500 to-teal-600', textColor: 'text-cyan-900 dark:text-cyan-100', subtextColor: 'text-cyan-700 dark:text-cyan-200', bottomGradient: 'from-cyan-200 via-teal-200 to-emerald-200 dark:from-cyan-800 dark:via-teal-800 dark:to-emerald-800' },
            features: { gradient: 'from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20', iconGradient: 'from-purple-500 to-violet-600', textColor: 'text-purple-900 dark:text-purple-100', subtextColor: 'text-purple-700 dark:text-purple-200', bottomGradient: 'from-purple-200 via-violet-200 to-indigo-200 dark:from-purple-800 dark:via-violet-800 dark:to-indigo-800' }
          };
          const colors = categoryColors[category as keyof typeof categoryColors] || categoryColors.content;
          
          return (
            <Card key={category} className="bg-white rounded-xl border border-gray-200/50 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
              <div 
                className={`p-6 border-b bg-gradient-to-r ${colors.gradient} cursor-pointer transition-all duration-200`}
                onClick={() => toggleSection(category)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${colors.iconGradient} rounded-full flex items-center justify-center shadow-lg`}>
                      {getCategoryIcon(category)}
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${colors.textColor} mb-1 capitalize`}>
                        {formatOperationName(category)}
                      </h3>
                      <p className={`text-sm ${colors.subtextColor}`}>
                        {category === 'content' && 'Costs for generating and managing content across all types'}
                        {category === 'media' && 'Costs for image, video, and audio operations'}
                        {category === 'processing' && 'Costs for data processing and analysis operations'}
                        {category === 'api' && 'Costs for API calls and external service integrations'}
                        {category === 'features' && 'Costs for advanced platform features and automation'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`bg-white/80 ${colors.textColor.replace('dark:text-', 'border-').replace('text-', 'border-')}-200`}>
                      {Object.keys(categoryData).length} items
                    </Badge>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform" />
                    )}
                  </div>
                </div>
              </div>
              
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <CardContent className="p-6">
                  <div className="space-y-1">
                    {renderNestedCosts(categoryData)}
                  </div>
                </CardContent>
              </div>
              
              <div className={`h-2 bg-gradient-to-r ${colors.bottomGradient}`} />
            </Card>
          );
        })}
      </div>

      {/* Enhanced Credit Packages */}
      {packages.length > 0 && (
        <Card className="bg-white rounded-xl border border-gray-200/50 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
          <div className="p-6 border-b bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center shadow-lg">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-pink-900 dark:text-pink-100 mb-1">
                  Credit Packages
                </h3>
                <p className="text-sm text-pink-700 dark:text-pink-200">
                  Available credit packages for purchase with competitive pricing
                </p>
              </div>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {packages.map((pkg, index) => {
                const monthsOfCredits = pkg.credits / 10000; // Assuming average 10k credits per month
                return (
                  <div
                    key={index}
                    className={`relative p-6 rounded-xl border-2 transition-all duration-300 hover:shadow-lg ${
                      pkg.popular 
                        ? 'border-pink-300 bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 shadow-md' 
                        : 'border-gray-200 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 hover:border-pink-200'
                    }`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-pink-500 text-white px-3 py-1 text-xs font-semibold">
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    <div className="text-center space-y-3">
                      <div className="font-bold text-xl text-gray-900 dark:text-gray-100">
                        {pkg.label}
                      </div>
                      <div className="text-3xl font-bold text-pink-600 dark:text-pink-400">
                        ${pkg.price}
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                          {pkg.credits.toLocaleString()} credits
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ${(pkg.price / pkg.credits * 1000).toFixed(1)}/1k credits
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded px-2 py-1">
                          ~{monthsOfCredits.toFixed(1)} months average
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
          <div className="h-2 bg-gradient-to-r from-pink-200 via-rose-200 to-red-200 dark:from-pink-800 dark:via-rose-800 dark:to-red-800" />
        </Card>
      )}
    </div>
  );
}